import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useQuizzes } from '@/hooks/useQuizzes';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const WIDGET_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://api.99fingers.com';

function generateEmbedCode(tenantSlug: string, quizId: string): string {
  return `<!-- 99fingers Quiz-Widget -->
<script src="${WIDGET_BASE_URL}/widget/v1/loader.js" async></script>
<nnf-quiz
  data-tenant="${tenantSlug}"
  data-quiz="${quizId}"
  data-api="${WIDGET_BASE_URL}"
></nnf-quiz>`;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="h-6 w-40 animate-pulse rounded bg-muted" />
        </CardHeader>
        <CardContent>
          <div className="h-10 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="h-6 w-32 animate-pulse rounded bg-muted" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="h-32 animate-pulse rounded bg-muted" />
          <div className="h-10 w-40 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>
    </div>
  );
}

export function EmbedPage() {
  const { data: settings, isLoading: settingsLoading } = useTenantSettings();
  const { data: quizzes, isLoading: quizzesLoading } = useQuizzes();

  const publishedQuizzes = useMemo(
    () => (quizzes ?? []).filter((q) => q.status === 'PUBLISHED'),
    [quizzes],
  );

  const [selectedQuizId, setSelectedQuizId] = useState<string>('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (publishedQuizzes.length > 0 && !selectedQuizId) {
      setSelectedQuizId(publishedQuizzes[0].quizId);
    }
  }, [publishedQuizzes, selectedQuizId]);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const isLoading = settingsLoading || quizzesLoading;
  const tenantSlug = settings?.slug ?? '';
  const embedCode =
    selectedQuizId && tenantSlug ? generateEmbedCode(tenantSlug, selectedQuizId) : '';

  async function handleCopy() {
    if (!embedCode) return;
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = embedCode;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">Einbettung</h1>
      <p className="mt-2 text-muted-foreground">
        Betten Sie Ihr Quiz in Ihren Online-Shop ein.
      </p>

      <div className="mt-6">
        {isLoading && <LoadingSkeleton />}

        {!isLoading && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quiz ausw&auml;hlen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {publishedQuizzes.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      Keine ver&ouml;ffentlichten Quizzes vorhanden.{' '}
                      <Link to="/quizzes" className="text-primary underline">
                        Ver&ouml;ffentlichen Sie zuerst ein Quiz unter Quizzes.
                      </Link>
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="quiz-select">Quiz</Label>
                      <select
                        id="quiz-select"
                        value={selectedQuizId}
                        onChange={(e) => setSelectedQuizId(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        {publishedQuizzes.map((q) => (
                          <option key={q.quizId} value={q.quizId}>
                            {q.title} (Ver&ouml;ffentlicht)
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-start gap-2 rounded-md bg-muted px-3 py-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">
                        Nur ver&ouml;ffentlichte Quizzes k&ouml;nnen eingebettet werden.
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {selectedQuizId && tenantSlug && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Embed-Code</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <pre className="overflow-x-auto rounded-md bg-muted p-4 text-sm leading-relaxed">
                    <code>{embedCode}</code>
                  </pre>
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={handleCopy}>
                      {copied ? (
                        <Check className="mr-2 h-4 w-4" />
                      ) : (
                        <Copy className="mr-2 h-4 w-4" />
                      )}
                      Code kopieren
                    </Button>
                    {copied && (
                      <p className="text-sm text-green-600">In Zwischenablage kopiert!</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Anleitung</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
                  <li>Kopieren Sie den Code oben.</li>
                  <li>
                    F&uuml;gen Sie ihn in Ihren Shop ein &mdash; idealerweise auf der Seite, wo
                    Kunden Produktberatung erwarten (z.B. Kategorie- oder Startseite).
                  </li>
                  <li>Das Quiz wird automatisch geladen und angezeigt.</li>
                </ol>
                <div className="mt-4 rounded-md bg-muted px-4 py-3">
                  <p className="text-sm font-medium">Shopware 6</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Administration &rarr; Einkaufswelten &rarr; HTML-Element hinzuf&uuml;gen &rarr;
                    Code einf&uuml;gen.
                  </p>
                </div>
                <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 dark:border-green-900 dark:bg-green-950">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    Hinweis f&uuml;r Ihren Datenschutzbeauftragten
                  </p>
                  <p className="mt-1 text-xs text-green-700 dark:text-green-300">
                    Das Widget arbeitet ohne Cookies und ohne personenbezogene Daten. Ein
                    Consent-Banner ist f&uuml;r die Quiz-Funktionalit&auml;t nicht erforderlich.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
